import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation.js';
import Footer from '../components/Footer.jsx';
import EventList from '../components/EventList.jsx';
import { brandText } from '../components/BrandText.jsx';
import { withLinks } from '../utils/textLinks.jsx';
import { localizePath } from '../utils/localizedRoutes.js';
import { getNearestEvents } from '../utils/events.js';
import { homepageImages } from '../assets/homepage/index.js';
import { brandImages } from '../assets/brand/index.js';
import { companyContact } from '../config/company.js';

// Prices are given on request: every product links to an enquiry by mail, an
// own product with a page of its own also to that page. The partner product is
// shown as such and links to its maker's site. Veridion is the flagship and
// gets its own band above the list.
const products = [
  { key: 'kyveil', image: homepageImages.kyveilLogo, path: '/kyveil' },
  { key: 'robuddy', image: homepageImages.robuddy, partner: true, href: 'https://www.robuddy.eu/' }
];

// The application a signed-in customer uses on the platform; it links to its
// page.
const platformApps = [
  { key: 'finance', path: '/finance' }
];

// Every focus of the company, in reading order of the three columns; a focus
// with a page of its own links there.
const services = [
  { key: 'veridion', path: '/veridion' },
  { key: 'custom' },
  { key: 'elektronik' },
  { key: 'quantum', path: '/quantum' },
  { key: 'dataanalysis' },
  { key: 'memory' },
  { key: 'kyveil', path: '/kyveil' },
  { key: 'agenticdev', path: '/agenticdev' },
  { key: 'finance', path: '/finance' },
  { key: 'consulting' }
];

// The start page shows the nearest events; the events page lists all of them.
const startPageEventCount = 3;

const LandingPage = () => {
  const { t, currentLang, getPageTranslations } = useTranslation('landing');
  const events = getPageTranslations('events');
  const nearestEvents = getNearestEvents(startPageEventCount);
  const country = companyContact.country[currentLang] || companyContact.country.en;

  return (
    <div className="min-h-screen bg-bg-main text-text-primary">
      <main className="signal-home">
        <section className="landing-hero">
          <h1 className="landing-hero__logo">
            <img src={brandImages.logo} alt={t.hero?.title} />
          </h1>
          <div className="landing-hero__text">
            <div>
              <p className="landing-hero__claim">{t.hero?.subtitle}</p>
              {t.hero?.vision && <p className="landing-hero__vision">{t.hero.vision}</p>}
            </div>
            <div className="landing-hero__copy">
              <p>{withLinks(t.hero?.intro || '', currentLang)}</p>
              <p>{withLinks(t.hero?.detail || '', currentLang)}</p>
            </div>
          </div>
        </section>

        {t.partners?.items?.bisigma && (
          <section className="signal-section" aria-labelledby="landing-partners-title">
            <header className="signal-section-header">
              <h2 id="landing-partners-title">{t.partners.title}</h2>
              {t.partners.subtitle && <p>{brandText(t.partners.subtitle)}</p>}
            </header>
            <div className="product-list">
              <article className="partner-row" aria-label={t.partners.items.bisigma.name}>
                <a
                  href="https://bisigma.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="partner-row__mark"
                  aria-label={t.partners.items.bisigma.name}
                >
                  <img src={homepageImages.bisigmaLogo} alt={t.partners.items.bisigma.name} />
                </a>
                <div className="partner-row__body">
                  {t.partners.items.bisigma.tagline && (
                    <p className="partner-row__tagline">
                      {String(t.partners.items.bisigma.tagline).split('\n').map((line, i) => (
                        <span key={i} className="block">{line}</span>
                      ))}
                    </p>
                  )}
                  <h3>{t.partners.items.bisigma.name}</h3>
                  {(Array.isArray(t.partners.items.bisigma.description)
                    ? t.partners.items.bisigma.description
                    : [t.partners.items.bisigma.description]
                  ).map((paragraph, index) => (
                    <p key={index}>{brandText(paragraph)}</p>
                  ))}
                  <a
                    className="site-text-link partner-row__link"
                    href="https://bisigma.de"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.partners.items.bisigma.cta}
                  </a>
                </div>
              </article>
            </div>
          </section>
        )}

        {t.products && (
          <section className="signal-section">
            <header className="signal-section-header">
              <h2>{t.products.title}</h2>
              <p>{withLinks(t.products.subtitle || '', currentLang)}</p>
            </header>
            {t.products.items?.veridion && (
              <article className="homepage-card p-6 md:p-8 mb-10 bg-bg-hover" aria-label={t.products.items.veridion.name}>
                <div className="grid md:grid-cols-[10rem_1fr] gap-6 md:gap-10 items-center">
                  <div className="product-row__mark product-row__mark--veridion w-full max-w-[10rem]">
                    <img src={homepageImages.veridionLogo} alt={t.products.items.veridion.name} />
                  </div>
                  <div>
                    <p className="editorial-kicker mb-2">{t.products.items.veridion.name}</p>
                    <h3 className="text-3xl md:text-4xl font-black tracking-[-0.04em] leading-[1.02] text-text-primary mb-3">
                      {t.products.items.veridion.claim}
                    </h3>
                    <div className="space-y-3 text-text-secondary leading-relaxed mb-4 max-w-4xl">
                      {(Array.isArray(t.products.items.veridion.text)
                        ? t.products.items.veridion.text
                        : [t.products.items.veridion.text]
                      ).map((paragraph, index) => (
                        <p key={index}>{withLinks(paragraph, currentLang)}</p>
                      ))}
                    </div>
                    {t.products.items.veridion.points?.length > 0 && (
                      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mb-5 max-w-3xl">
                        {t.products.items.veridion.points.map((point, index) => (
                          <div key={index} className="flex items-start gap-2 text-sm font-black text-text-primary">
                            <span className="text-primary" aria-hidden="true">+</span>
                            <span>{point}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <span className="product-row__price">{t.products.priceOnRequest}</span>
                      <Link className="site-text-link" to={localizePath('/veridion', currentLang)}>{t.products.items.veridion.cta}</Link>
                      <a
                        className="site-text-link"
                        href={`mailto:${companyContact.email}?subject=${encodeURIComponent(t.products.items.veridion.name)}`}
                      >
                        {t.products.request}
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            )}
            <div className="product-list">
              {products.map((product) => {
                const data = t.products.items?.[product.key];
                if (!data) return null;

                return (
                  <article className="product-row" key={product.key}>
                    <div className={`product-row__mark product-row__mark--${product.key}`}>
                      <img src={product.image} alt={data.name} />
                    </div>
                    <div className="product-row__body">
                      {product.partner && <p className="editorial-kicker">{t.products.partnerLabel}</p>}
                      <h3>{data.name}</h3>
                      {data.description?.map((paragraph, index) => (
                        <p key={index}>{withLinks(paragraph, currentLang)}</p>
                      ))}
                      {data.vendorNote && <p className="product-row__note">{brandText(data.vendorNote)}</p>}
                    </div>
                    <div className="product-row__offer">
                      <p className="product-row__price">{t.products.priceOnRequest}</p>
                      <a className="site-text-link" href={`mailto:${companyContact.email}?subject=${encodeURIComponent(data.name)}`}>{t.products.request}</a>
                      {product.path && (
                        <Link className="site-text-link" to={localizePath(product.path, currentLang)}>{data.cta}</Link>
                      )}
                      {product.href && (
                        <a className="site-text-link" href={product.href} target="_blank" rel="noopener noreferrer">{data.cta}</a>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {t.platformNote && (
          <section className="signal-section" aria-labelledby="platform-note-title">
            <header className="signal-section-header">
              <h2 id="platform-note-title">{t.platformNote.title}</h2>
              <p>{t.platformNote.description}</p>
            </header>
            <div className="platform-apps">
              {platformApps.map((app) => {
                const data = t.platformNote.apps?.[app.key];
                if (!data) return null;

                return (
                  <article className="platform-app" key={app.key}>
                    <h3>{data.name}</h3>
                    <div>
                      <p>{data.text}</p>
                      {app.path && (
                        <Link className="site-text-link platform-app__link" to={localizePath(app.path, currentLang)}>{data.cta}</Link>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        <section className="signal-section">
          <header className="signal-section-header">
            <h2>{t.services?.title}</h2>
          </header>
          <div className="service-grid">
            {services.map((service) => {
              const data = t.services?.[service.key];
              if (!data) return null;

              return (
                <article className="service-cell" key={service.key}>
                  <h3>{data.title}</h3>
                  <p>{brandText(data.description)}</p>
                  {data.features?.length > 0 && (
                    <p className="service-cell__features">{data.features.join(' · ')}</p>
                  )}
                  {service.path && data.cta && (
                    <Link className="site-text-link service-cell__link" to={localizePath(service.path, currentLang)}>{data.cta}</Link>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        {nearestEvents.length > 0 && (
          <section className="signal-section">
            <header className="signal-section-header">
              <h2>{events.title}</h2>
            </header>
            <EventList events={nearestEvents} texts={events.items} language={currentLang} />
            <Link className="site-text-link landing-events__all" to={localizePath('/events', currentLang)}>{events.all}</Link>
          </section>
        )}

        {t.contact && (
          <section className="landing-contact" aria-labelledby="landing-contact-title">
            <div>
              <h2 id="landing-contact-title" className="site-heading">{t.contact.title}</h2>
              <a className="homepage-button-primary landing-contact__cta" href={`mailto:${companyContact.email}`}>{t.contact.cta}</a>
            </div>
            <dl className="landing-contact__facts">
              <div>
                <dt>{t.contact.addressLabel}</dt>
                <dd>
                  {brandText(t.nav?.title)}<br />
                  {companyContact.street}<br />
                  {companyContact.postalCode} {companyContact.city}<br />
                  {country}
                </dd>
              </div>
              <div>
                <dt>{t.contact.phoneLabel}</dt>
                <dd>
                  {companyContact.phones.map((phone) => (
                    <a key={phone} className="landing-contact__phone" href={`tel:${phone.replace(/\s+/g, '')}`}>{phone}</a>
                  ))}
                </dd>
              </div>
              <div>
                <dt>{t.contact.emailLabel}</dt>
                <dd><a href={`mailto:${companyContact.email}`}>{brandText(companyContact.email)}</a></dd>
              </div>
            </dl>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;
